/**
 * 「未来 30 天现金流」卡（**J 域首页** · 批 `130` · 负责人 2026-10-31 第 7 条）
 *
 * 依据：`nucleus/cash-coverage.ts`（唯一算法）· `contract/cashflow.ts`（形状）·
 * `features/cashflow.ts`（三态文案）· K 域 PRD §4.6.3（窗口内推演允许 / 跨窗口预测禁止）。
 *
 * ## 🔴 本组件**一个数都不算**
 *
 * | 显示 | 来源 |
 * |---|---|
 * | 可用现金 | 服务端 `liquidCents`（已折算本币） |
 * | 未来 30 天必付 | 服务端 `upcomingTotalCents`（F 域唯一聚合） |
 * | 最低点 / 最低日 | 服务端 `lowestCents` / `lowestDay` |
 * | 缺口 | 服务端 `shortfallCents`（**不截断为 0**） |
 * | 够不够（三态） | 服务端 `kind` |
 *
 * ⇒ 本文件里**没有** `reduce`、没有除法、没有 `new Date()`、没有金额格式化（一律走 `<Amount>`）。
 *
 * ## 🔴 它和「本月剩余可花」卡是**两张卡、两个口径**
 *
 * 那张的分母是**预算额度**（"这个月还能花多少"），这张的分母是**账户余额**
 * （"下个月付不付得出"）。`DR-010` 决策三已把两者列为**禁共用函数**的名字；
 * 并排显示时靠副标题的口径行区分，**不合成一个数字**。
 *
 * ## ⚠️ 刻意不画「逐日余额曲线」
 *
 * 服务端下发了 `points`（逐日累计），但本卡**不画** —— 30 天里通常只有 3–8 个点，
 * 画成折线在首页这种宽度上会比数字更难读；而"哪天最紧"已经由「最低点（日期）」一行直说了。
 * 数据留着（契约与端侧 model 都保留），等将来有专门的现金流页时再用。
 */

import { Alert, Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CashCoverageKind } from '@jiaying/shared';
import { getCashCoverage } from '../api/endpoints.js';
import { Loading } from '../ui/Loading.js';
import { Amount } from '../ui/Amount.js';
import {
  CASHFLOW_ACCOUNTS_TITLE,
  CASHFLOW_LIQUID_LABEL,
  CASHFLOW_NO_LIQUID_TEXT,
  shouldShowNoLiquid,
  toCashflowCard,
} from '../features/cashflow.js';

export function CashCoverageCard() {
  const q = useQuery({ queryKey: ['cashflow-coverage'], queryFn: getCashCoverage });

  if (q.isPending) return <Loading py={3} />;
  if (q.isError) {
    return (
      <Alert severity="error" data-testid="cashflow-error">
        未来 30 天现金流读取失败：{String(q.error)}
      </Alert>
    );
  }

  const view = q.data;
  const m = toCashflowCard(view);
  const currency = view.baseCurrency;
  /** 只在真的有现金账户时列明细 —— 一条都没有时那是个空标题 */
  const showAccounts = m.accounts.length > 0;

  return (
    <Card data-testid="cashflow-card" data-kind={m.kind}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} data-testid="cashflow-title">
            {m.title}
          </Typography>
          {/* 🔴 口径行：把"不预测"写在脸上 —— 用户看到数字前先知道它的边界 */}
          <Typography variant="caption" color="text.secondary">
            {m.subtitle}
          </Typography>
        </Stack>

        {/* ── 主数值：可用现金 ── */}
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {CASHFLOW_LIQUID_LABEL}
          </Typography>
          <Amount
            cents={m.primaryCents as never}
            currency={currency}
            colored={false}
            variant="h5"
            data-testid="cashflow-liquid"
            sx={{ fontWeight: 700 }}
          />
          {/*
            结论短语：🔴 三态里 `noUpcoming` 刻意**不给结论** ——
            没有必付项时说"够付"是在回答一个没被问的问题。
          */}
          {m.verdict === null ? null : (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                // 铁律 8：红 = 正（这里"不够付"是预警，用朱红 `error.main` 而非绿色）
                color: m.verdictAlarming ? 'error.main' : 'text.primary',
              }}
              data-testid="cashflow-verdict"
            >
              {m.verdict}
            </Typography>
          )}
        </Stack>

        {/* ── 明细行（必付合计 / 最低点 / 缺口）── */}
        {m.lines.length === 0 ? null : (
          <Box sx={{ mt: 1 }} data-testid="cashflow-lines">
            {m.lines.map((line) => (
              <Stack
                key={line.key}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={1}
                sx={{ py: 0.25 }}
                data-testid={`cashflow-line-${line.key}`}
              >
                <Typography variant="caption" color="text.secondary">
                  {line.label}
                </Typography>
                {line.cents === null ? (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                ) : (
                  <Amount
                    cents={line.cents as never}
                    currency={currency}
                    variant="caption"
                    colored={false}
                    sx={line.alarming ? { color: 'error.main', fontWeight: 600 } : undefined}
                  />
                )}
              </Stack>
            ))}
          </Box>
        )}

        {/* ── 缺口建议：🔴 只说"哪天之前、补多少"，不给处置方式（见 features/cashflow.ts 红线）── */}
        {m.adviceLead === null || m.adviceCents === null ? null : (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="cashflow-advice">
            {m.adviceLead}{' '}
            <Amount cents={m.adviceCents as never} currency={currency} variant="caption" colored={false} />
          </Alert>
        )}

        {/* ── 没有必付：说清"不是没算"而是"确实没有"── */}
        {m.emptyText === null ? null : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="cashflow-empty">
            {m.emptyText}
          </Typography>
        )}

        {/* ── 没有可用现金：解释为什么是 0（否则用户会以为卡片坏了）── */}
        {shouldShowNoLiquid(view) && m.kind !== CashCoverageKind.NoUpcoming ? (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="cashflow-no-liquid">
            {CASHFLOW_NO_LIQUID_TEXT}
          </Alert>
        ) : null}

        {/* 🔴 缺汇率 ⇒ 必须标注（`DR-004`：不静默、不按 1:1 冒充）── */}
        {m.fxNotice === null ? null : (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="cashflow-fx-notice">
            {m.fxNotice}
          </Alert>
        )}

        {/* ── 钱在哪（逐账户）── */}
        {showAccounts ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" component="div">
              {CASHFLOW_ACCOUNTS_TITLE}
            </Typography>
            <Box sx={{ mt: 0.5 }} data-testid="cashflow-accounts">
              {m.accounts.map((a) => (
                <Stack
                  key={a.accountId}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  spacing={1}
                  sx={{ py: 0.25 }}
                  data-testid={`cashflow-account-${a.accountId}`}
                >
                  <Typography variant="caption" noWrap>
                    {a.name}
                  </Typography>
                  {/* 🔴 原币呈现（多币种家庭要能对上自己的卡 —— `DR-036` 金额必带币种） */}
                  {a.baseCents === null ? (
                    <Typography variant="caption" color="text.secondary" title="缺少汇率，未计入合计">
                      —{' '}
                      <Amount cents={a.originalCents as never} currency={a.currency} variant="caption" colored={false} />
                    </Typography>
                  ) : (
                    <Amount
                      cents={a.originalCents as never}
                      currency={a.currency}
                      variant="caption"
                      colored={false}
                    />
                  )}
                </Stack>
              ))}
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
